import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../components/ui/sonner';
import logger from '../lib/logger';
import { getErrorMessage } from '../lib/errorHandler';

const AuthContext = createContext(null);

const getBackendUrl = () => {
  // Prioridad 1: Variable de entorno (para producción)
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }

  const { hostname, protocol } = window.location;
  
  // Si estamos en un dominio que no es localhost o IP privada, 
  // probablemente estamos en producción y el backend está en el mismo dominio o subdominio
  if (hostname !== 'localhost' && !hostname.match(/^192\.168\./)) {
    // Asumimos que en producción el API está en el mismo host o configurado vía Env Var
    return `${protocol}//${hostname}`; 
  }

  // Desarrollo local
  return `${protocol}//${hostname}:4001`;
};

export const BACKEND_URL = getBackendUrl();
export const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

// Crear instancia de axios configurada
export const apiClient = axios.create({
  baseURL: API,
});

// Keep track of user state outside for the interceptor to access synchronously
let currentUser = null;
let currentSupportUser = null;

const getToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');

try {
  const storedSupportUser = localStorage.getItem('supportUser') || sessionStorage.getItem('supportUser');
  if (storedSupportUser) {
    currentSupportUser = JSON.parse(storedSupportUser);
  }
} catch (e) {
  logger.error('Error parsing supportUser from storage:', e);
}

// Interceptor para Modo Soporte (Admin actuando como otro usuario)
// This is set up once at the module level to avoid useEffect race conditions
apiClient.interceptors.request.use((config) => {
  // Add token if exists
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add user_id if in support mode and user is admin
  if (currentSupportUser && currentUser?.rol === 'admin') {
    config.params = {
      ...config.params,
      user_id: currentSupportUser.id
    };
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(getToken());
  const [supportUser, setSupportUser] = useState(currentSupportUser);
  const [maintenanceMode, setMaintenanceMode] = useState(null); // { active, message }
  const [loginMaintenance, setLoginMaintenance] = useState(false); // Flag para cuando el 503 viene del login
  
  const isAdmin = user?.rol === 'admin';

  // Sync local variables with state changes
  useEffect(() => {
    currentUser = user;
  }, [user]);

  useEffect(() => {
    currentSupportUser = supportUser;
  }, [supportUser]);

  // Global Confirm Dialog State
  const [confirmConfig, setConfirmConfig] = useState(null);

  const showNotification = (message, type = 'success') => {
    if (type === 'error') {
      toast.error(message);
    } else {
      toast.success(message);
    }
  };

  const confirm = useCallback((config) => {
    return new Promise((resolve) => {
      setConfirmConfig({
        ...config,
        onConfirm: () => {
          setConfirmConfig(null);
          resolve(true);
        },
        onClose: () => {
          setConfirmConfig(null);
          resolve(false);
        }
      });
    });
  }, []);

  // Update axios defaults when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
      delete apiClient.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Global response interceptor for maintenance and session invalidation
  useEffect(() => {
    const interceptor = apiClient.interceptors.response.use(
      (response) => {
        // Si la respuesta es exitosa pero estábamos en modo mantenimiento, resetearlo
        if (maintenanceMode?.active) {
            setMaintenanceMode(null);
        }
        return response;
      },
      (error) => {
        // 503 Service Unavailable -> Modo Mantenimiento
        if (error.response?.status === 503) {
          setMaintenanceMode({
            active: true,
            message: error.response.data.detail || 'El sistema se encuentra en mantenimiento.'
          });
        }
        
        // 401 Unauthorized -> Sesión invalidada o expirada
        if (error.response?.status === 401) {
          // Si el mensaje indica invalidación remota, forzar logout
          if (error.response.data.detail === "Sesión invalidada remotamente") {
            showNotification('Tu sesión ha sido cerrada remotamente', 'warning');
            logout();
          } else if (token) {
            // Token expirado u otros 401
            logout();
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      apiClient.interceptors.response.eject(interceptor);
    };
  }, [token, maintenanceMode]);

  // Cargar usuario al iniciar
  useEffect(() => {
    cargarMantenimiento();
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const cargarMantenimiento = async () => {
    try {
      const response = await axios.get(`${API}/system/maintenance`);
      if (response.data) {
        setMaintenanceMode({
          active: response.data.active,
          message: response.data.message,
          effective_at: response.data.effective_at,
          seconds_remaining: response.data.seconds_remaining,
        });
      }
    } catch (error) {
      logger.error('Error inicial de mantenimiento:', error);
    }
  };

  // Polling para modo mantenimiento (solo para usuarios no-admin logueados).
  // Cada 10s para detectar rápidamente la activación del grace period y
  // permitir el countdown + auto-save antes del logout forzado.
  useEffect(() => {
    let interval;
    if (user && !isAdmin) {
      interval = setInterval(() => {
        cargarMantenimiento();
      }, 10000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [user, isAdmin]);

  const loadUser = async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      logger.error('Error cargando usuario:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const response = await axios.post(`${API}/auth/login`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const newToken = response.data.access_token;
      
      if (rememberMe) {
          localStorage.setItem('token', newToken);
      } else {
          sessionStorage.setItem('token', newToken);
      }

      // Set axios headers immediately before calling loadUser
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      logger.log('Token configurado en login:', newToken.substring(0, 20) + '...');

      setToken(newToken);
      setLoginMaintenance(false); // Resetear flag de mantenimiento en login exitoso

      // Cargar datos del usuario
      await loadUser();

      return { success: true };
    } catch (error) {
      logger.error('Error en login:', error);
      
      if (error.response?.status === 503) {
          setLoginMaintenance(true);
          setMaintenanceMode({
              active: true,
              message: error.response.data.detail || 'El sistema se encuentra en mantenimiento.'
          });
      }

      return {
        success: false,
        error: error.response?.data?.detail || 'Error al iniciar sesión'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('supportUser');
    sessionStorage.removeItem('supportUser');
    setToken(null);
    setUser(null);
    setSupportUser(null);
    setLoginMaintenance(false);
    delete axios.defaults.headers.common['Authorization'];
  };

  const startSupport = (targetUser) => {
    setSupportUser(targetUser);
    const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
    storage.setItem('supportUser', JSON.stringify(targetUser));
  };

  const stopSupport = () => {
    setSupportUser(null);
    localStorage.removeItem('supportUser');
    sessionStorage.removeItem('supportUser');
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email,
        frontend_url: window.location.origin
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Error al enviar correo'
      };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Error al restablecer contraseña'
      };
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: oldPassword,
        new_password: newPassword
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Error al cambiar contraseña'
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    isAuthenticated: !!user,
    isAdmin: user?.rol === 'admin',
    apiClient, // Add apiClient to the context value
    showNotification,
    supportUser,
    startSupport,
    stopSupport,
    isSupportMode: !!supportUser && user?.rol === 'admin',
    confirm,
    confirmConfig,
    loadUser,
    maintenanceMode,
    setMaintenanceMode,
    loginMaintenance,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};

export default AuthContext;
